import { useEffect, useRef } from 'react';
import { Button, Card, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  GETTING_STARTED_STEPS,
  type GettingStartedStep,
  useGettingStartedStore,
} from '@shared/getting-started';

const { Title, Text, Paragraph } = Typography;

/** Плейсхолдер скриншота: серый блок с подписью, пока картинки нет. */
function ScreenshotBlock({ step }: { step: GettingStartedStep }) {
  if (!step.screenshot) {
    return (
      <div
        style={{
          marginTop: 16,
          height: 220,
          borderRadius: 12,
          border: '1px dashed #E5E7EB',
          background: '#F9FAFB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Скриншот появится здесь
        </Text>
      </div>
    );
  }
  return (
    <img
      src={`/getting-started/${step.screenshot}`}
      alt={step.title}
      style={{
        marginTop: 16,
        width: '100%',
        borderRadius: 12,
        border: '1px solid #E5E7EB',
        display: 'block',
      }}
      // Если файл скриншота ещё не залит — прячем картинку, не показываем «битый» значок.
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = 'none';
      }}
    />
  );
}

export function GettingStartedPage() {
  const navigate = useNavigate();
  const setActiveStep = useGettingStartedStore((s) => s.setActiveStep);
  const requestedStep = useGettingStartedStore((s) => s.requestedStep);
  const requestNonce = useGettingStartedStore((s) => s.requestNonce);

  // Рефы на секции — для скролла по запросу из сайдбара.
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Подсветка активной секции при скролле.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Берём самую верхнюю из пересекающих область видимости секций.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveStep(visible[0].target.id);
        }
      },
      {
        // Сужаем «активную зону» к верхней трети экрана — секция считается
        // активной, когда её заголовок подходит к верху, а не когда она едва влезла снизу.
        rootMargin: '-20% 0px -60% 0px',
        threshold: 0,
      },
    );

    GETTING_STARTED_STEPS.forEach((step) => {
      const el = sectionRefs.current[step.id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [setActiveStep]);

  // Скролл к секции по клику в сайдбаре. Зависим от nonce, чтобы повторный
  // клик по тому же этапу тоже срабатывал.
  useEffect(() => {
    if (!requestedStep) return;
    const el = sectionRefs.current[requestedStep];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedStep, requestNonce]);

  // Сброс активного этапа при уходе со страницы — чтобы сайдбар не подсвечивал
  // этап, когда юзер уже на другой странице.
  useEffect(() => {
    return () => setActiveStep(null);
  }, [setActiveStep]);

  return (
    <div style={{ maxWidth: 820 }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Обучение
      </Title>
      <Text type="secondary">
        Пять шагов от создания ассистента до запуска с реальными клиентами.
      </Text>

      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {GETTING_STARTED_STEPS.map((step) => (
          <div
            key={step.id}
            id={step.id}
            ref={(el) => {
              sectionRefs.current[step.id] = el;
            }}
            // Отступ сверху, чтобы при скролле заголовок не прилипал к самому верху Content.
            style={{ scrollMarginTop: 16 }}
          >
            <Card>
              <Title level={3} style={{ marginTop: 0, fontWeight: 500 }}>
                {step.title}
              </Title>
              {step.paragraphs.map((p, i) => (
                <Paragraph key={i} style={{ marginBottom: i === step.paragraphs.length - 1 ? 0 : 12 }}>
                  {p}
                </Paragraph>
              ))}
              <ScreenshotBlock step={step} />
              {step.action && (
                <div style={{ marginTop: 16 }}>
                  <Button onClick={() => navigate(step.action!.to)}>
                    {step.action.label}
                  </Button>
                </div>
              )}
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}
